import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock3, Download, RefreshCw, Search, WalletCards, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { paymentsAPI } from '../services/api';

const EMPTY_FILTERS = { search: '', kind: '', status: '', provider: '', dateFrom: '', dateTo: '', attention: '' };
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const PAID = new Set(['paid', 'completed', 'complete', 'success', 'succeeded', 'active']);
const FAILED = new Set(['failed', 'failure', 'error', 'cancelled', 'canceled', 'rejected']);
const MOBILE_MONEY_COUNTRIES = [
  {
    code: 'CM',
    name: 'Cameroun',
    providers: [
      { name: 'MTN Mobile Money', key: 'paynote_momo_cm', status: 'active', note: 'Paynote production' },
      { name: 'Orange Money', key: 'paynote_om_cm', status: 'pending', note: 'Identifiants API attendus' },
    ],
  },
  {
    code: 'GA',
    name: 'Gabon',
    providers: [
      { name: 'Airtel Money', key: 'airtel_ga', status: 'planned' },
      { name: 'Moov Money', key: 'moov_ga', status: 'planned' },
    ],
  },
  {
    code: 'TD',
    name: 'Tchad',
    providers: [
      { name: 'Airtel Money', key: 'airtel_td', status: 'planned' },
      { name: 'Mobile Money', key: 'momo_td', status: 'planned' },
    ],
  },
  {
    code: 'CG',
    name: 'Congo',
    providers: [
      { name: 'Airtel Money', key: 'airtel_cg', status: 'planned' },
      { name: 'MTN Mobile Money', key: 'mtn_cg', status: 'planned' },
    ],
  },
  {
    code: 'CF',
    name: 'République centrafricaine',
    providers: [
      { name: 'Orange Money', key: 'orange_cf', status: 'planned' },
      { name: 'Telecel Money', key: 'telecel_cf', status: 'planned' },
    ],
  },
  {
    code: 'GQ',
    name: 'Guinée équatoriale',
    providers: [
      { name: 'Orange Money', key: 'orange_gq', status: 'planned' },
      { name: 'Mobile Money', key: 'momo_gq', status: 'planned' },
    ],
  },
];

function providerStatus(provider) {
  if (provider.status === 'active') return { label: 'ACTIF', color: '#22c55e', background: '#22c55e22', icon: CheckCircle2 };
  if (provider.status === 'pending') return { label: 'EN ATTENTE API', color: '#f5a623', background: '#f5a62322', icon: Clock3 };
  return { label: 'À INTÉGRER', color: '#58a6ff', background: '#58a6ff22', icon: Clock3 };
}

function formatMoney(value) {
  return `${new Intl.NumberFormat('fr-FR').format(Number(value) || 0)} FCFA`;
}

function formatDate(value) {
  if (!value) return 'n/a';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'n/a' : date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

export function statusTone(value) {
  const status = String(value || '').toLowerCase();
  if (PAID.has(status)) return { color: '#22c55e', background: '#22c55e22' };
  if (FAILED.has(status)) return { color: '#e63946', background: '#e6394622' };
  return { color: '#f5a623', background: '#f5a62322' };
}

function formatPendingDuration(minutes) {
  const total = Math.max(0, Number(minutes) || 0);
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours < 24) return mins ? `${hours} h ${mins} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days} j ${restHours} h` : `${days} j`;
}

export function paymentFollowUp(row) {
  if (String(row?.status || '').toLowerCase() === 'stale_pending') {
    return { label: `Stale pending (${formatPendingDuration(row.pending_minutes)})`, tone: 'warn' };
  }
  if (row?.needs_attention) return { label: `À vérifier (${formatPendingDuration(row.pending_minutes)})`, tone: 'warn' };
  if (row?.kind === 'subscription' && PAID.has(String(row?.status || '').toLowerCase())) {
    if (row?.subscription_applied) {
      return { label: `Actif jusqu'au ${formatDate(row.current_subscription_ends_at)}`, tone: 'ok' };
    }
    return { label: 'Payé, abonnement à contrôler', tone: 'bad' };
  }
  if (row?.pending_minutes != null) return { label: `En attente ${formatPendingDuration(row.pending_minutes)}`, tone: 'pending' };
  return { label: 'OK', tone: 'neutral' };
}

function followUpTone(tone) {
  if (tone === 'ok') return { color: '#22c55e', background: '#22c55e22' };
  if (tone === 'bad') return { color: '#e63946', background: '#e6394622' };
  if (tone === 'warn' || tone === 'pending') return { color: '#f5a623', background: '#f5a62322' };
  return { color: 'rgba(255,255,255,.7)', background: 'rgba(255,255,255,.08)' };
}

function csvCell(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

export function maskSensitiveIdentifier(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return 'n/a';
  if (typeof value === 'number' && !Number.isFinite(value)) return 'n/a';
  const clean = String(value).trim();
  if (!clean) return 'n/a';
  const visible = clean.length > 4 ? clean.slice(-4) : '';
  return `${'•'.repeat(Math.max(4, clean.length - visible.length))}${visible}`;
}

export function paymentExportCells(row) {
  return [
    row?.created_at,
    row?.parent_name,
    maskSensitiveIdentifier(row?.parent_phone),
    row?.kind,
    row?.product,
    row?.amount_xaf,
    row?.payment_method,
    row?.provider,
    row?.status,
    paymentFollowUp(row).label,
    maskSensitiveIdentifier(row?.external_reference),
    maskSensitiveIdentifier(row?.provider_reference),
    row?.country_code,
  ];
}

export default function PaymentsPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [providers, setProviders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  async function load(next = applied, nextPage = page, limit = pageSize) {
    setLoading(true);
    try {
      const { data } = await paymentsAPI.list({ ...next, page: nextPage, limit });
      if (!data.ok) throw new Error(data.error || 'Lecture des paiements impossible');
      setRows(data.transactions || []);
      setSummary(data.summary || {});
      setProviders(data.providers || []);
      setTotal(Number(data.total) || 0);
      setPage(Number(data.page) || nextPage);
      return data.transactions || [];
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erreur de chargement');
      return [];
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(EMPTY_FILTERS, 1, pageSize); }, []);

  const kpis = useMemo(() => [
    ['Montant encaissé', formatMoney(summary.paidAmountXaf), '#22c55e'],
    ['Transactions payées', summary.paidCount || 0, '#22c55e'],
    ['En attente', summary.pendingCount || 0, '#f5a623'],
    ['Stale pending', summary.stalePendingCount || 0, '#f59e0b'],
    ['À vérifier', summary.attentionCount || 0, '#f5a623'],
    ['Échecs', summary.failedCount || 0, '#e63946'],
    ['Abonnements', formatMoney(summary.subscriptionAmountXaf), '#58a6ff'],
    ['Packs SMS', formatMoney(summary.smsAmountXaf), '#a371f7'],
  ], [summary]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = total === 0 ? 0 : ((page - 1) * pageSize) + 1;
  const pageEnd = Math.min(total, page * pageSize);

  function submit(event) {
    event.preventDefault();
    const next = { ...filters };
    setApplied(next);
    setPage(1);
    load(next, 1, pageSize);
  }

  function reset() {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setDetail(null);
    setPage(1);
    load(EMPTY_FILTERS, 1, pageSize);
  }

  function showAttentionOnly() {
    const next = { ...filters, attention: filters.attention ? '' : '1', status: filters.attention ? filters.status : 'pending' };
    setFilters(next);
    setApplied(next);
    setPage(1);
    load(next, 1, pageSize);
  }

  function changePageSize(event) {
    const nextSize = Number(event.target.value) || 20;
    setPageSize(nextSize);
    setPage(1);
    load(applied, 1, nextSize);
  }

  function goToPage(nextPage) {
    const boundedPage = Math.min(totalPages, Math.max(1, nextPage));
    if (boundedPage === page) return;
    setPage(boundedPage);
    load(applied, boundedPage, pageSize);
  }

  function closeDetail() {
    setDetail(null);
  }

  async function exportCsv() {
    try {
      const { data } = await paymentsAPI.list({ ...applied, page: 1, limit: 500 });
      if (!data.ok) throw new Error(data.error || 'Export impossible');
      const exportRows = data.transactions || [];
      if (!exportRows.length) return toast.error('Aucune transaction à exporter.');
      const headers = ['Date', 'Client', 'Téléphone', 'Type', 'Produit', 'Montant FCFA', 'Moyen', 'Fournisseur', 'Statut', 'Suivi', 'Référence TOKKAM', 'Référence fournisseur', 'Pays'];
      const lines = exportRows.map(r => paymentExportCells(r).map(csvCell).join(';'));
      const blob = new Blob([`\uFEFF${headers.map(csvCell).join(';')}\n${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tokkam-paiements-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Export impossible');
    }
  }

  return (
    <div style={S.page}>
      <section style={S.providersSection}>
        <div style={S.sectionHead}>
          <div>
            <h2 style={S.sectionTitle}>Fournisseurs Mobile Money</h2>
            <p style={S.sectionText}>Catalogue administratif Afrique Centrale. Les fournisseurs non configurés restent masqués dans les applications mobiles.</p>
          </div>
          <WalletCards size={24} color="#f5a623"/>
        </div>
        <div style={S.providerGrid}>
          {MOBILE_MONEY_COUNTRIES.map(country => (
            <article key={country.code} style={S.countryCard}>
              <div style={S.countryHead}>
                <span style={S.countryCode}>{country.code}</span>
                <strong style={S.countryName}>{country.name}</strong>
              </div>
              {country.providers.map(provider => {
                const status = providerStatus(provider);
                const StatusIcon = status.icon;
                return (
                  <div key={provider.key} style={S.providerRow}>
                    <div style={S.providerIdentity}>
                      <strong style={S.providerName}>{provider.name}</strong>
                      <span style={S.providerKey}>{provider.key}</span>
                      {provider.note && <span style={S.providerNote}>{provider.note}</span>}
                    </div>
                    <span style={{ ...S.providerStatus, color: status.color, background: status.background }}>
                      <StatusIcon size={12}/>{status.label}
                    </span>
                  </div>
                );
              })}
            </article>
          ))}
        </div>
      </section>

      <div style={S.kpis}>{kpis.map(([label, value, color]) => <div key={label} style={S.kpi}><span style={S.kpiLabel}>{label}</span><strong style={{ ...S.kpiValue, color }}>{value}</strong></div>)}</div>

      <form style={S.filters} onSubmit={submit}>
        <label style={S.searchWrap}><Search size={16}/><input style={S.search} value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="Client, téléphone ou référence..." /></label>
        <select style={S.control} value={filters.kind} onChange={e => setFilters(f => ({ ...f, kind: e.target.value }))}><option value="">Tous les produits</option><option value="subscription">Abonnements</option><option value="sms">Packs SMS</option></select>
        <select style={S.control} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}><option value="">Tous les statuts</option><option value="paid">Payé</option><option value="pending">En attente</option><option value="stale_pending">Stale pending</option><option value="failed">Échec</option></select>
        <select style={S.control} value={filters.provider} onChange={e => setFilters(f => ({ ...f, provider: e.target.value }))}><option value="">Tous les fournisseurs</option>{providers.map(p => <option key={p} value={p}>{p}</option>)}</select>
        <input title="Date de début" type="date" style={S.control} value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}/>
        <input title="Date de fin" type="date" style={S.control} value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}/>
        <button style={S.primary} disabled={loading}>{loading ? 'Lecture...' : 'Filtrer'}</button>
        <button type="button" style={S.iconBtn} title="Réinitialiser" onClick={reset}><X size={17}/></button>
        <button type="button" style={S.iconBtn} title="Rafraîchir" onClick={() => load(applied, page, pageSize)}><RefreshCw size={17}/></button>
        <button type="button" style={filters.attention ? S.attentionBtnActive : S.attentionBtn} onClick={showAttentionOnly}>
          <AlertTriangle size={16}/>{filters.attention ? 'Tout afficher' : 'À vérifier'}
        </button>
        <button type="button" style={S.exportBtn} onClick={exportCsv}><Download size={16}/> CSV</button>
      </form>

      <div style={S.meta}>{total} transaction{total > 1 ? 's' : ''} trouvée{total > 1 ? 's' : ''}. Les montants affichés comme encaissés correspondent uniquement aux statuts payés.</div>
      <div style={S.pagination}>
        <span style={S.pageInfo}>Affichage {pageStart}-{pageEnd} sur {total}</span>
        <label style={S.pageSizeLabel}>Lignes par page
          <select style={S.pageSizeSelect} value={pageSize} onChange={changePageSize}>
            {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <button type="button" style={S.pageButton} disabled={loading || page <= 1} onClick={() => goToPage(page - 1)}>Précédent</button>
        <span style={S.pageInfo}>Page {page} / {totalPages}</span>
        <button type="button" style={S.pageButton} disabled={loading || page >= totalPages} onClick={() => goToPage(page + 1)}>Suivant</button>
      </div>
      <div style={S.tableCard}><table style={S.table}><thead><tr>{['DATE', 'CLIENT', 'TYPE', 'PRODUIT', 'MONTANT', 'FOURNISSEUR', 'STATUT', 'SUIVI', 'RÉFÉRENCE'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead><tbody>
        {loading && <tr><td colSpan="9" style={S.empty}>Chargement depuis PostgreSQL...</td></tr>}
        {!loading && rows.map(row => {
          const followUp = paymentFollowUp(row);
          return <tr key={`${row.kind}-${row.id}`} style={row.needs_attention ? S.attentionTr : S.tr} onClick={() => setDetail(row)}>
          <td style={S.td}>{formatDate(row.created_at)}</td>
          <td style={S.td}><strong>{row.parent_name}</strong><div style={S.sub}>{maskSensitiveIdentifier(row.parent_phone)}</div></td>
          <td style={S.td}>{row.kind === 'sms' ? 'Pack SMS' : 'Abonnement'}</td>
          <td style={S.td}>{row.product || 'n/a'}</td>
          <td style={{ ...S.td, fontWeight: 800 }}>{formatMoney(row.amount_xaf)}</td>
          <td style={S.td}>{row.provider || row.payment_method || 'n/a'}</td>
          <td style={S.td}><span style={{ ...S.badge, ...statusTone(row.status) }}>{row.status || 'inconnu'}</span></td>
          <td style={S.td}><span style={{ ...S.badge, ...followUpTone(followUp.tone) }}>{followUp.label}</span></td>
          <td style={{ ...S.td, fontFamily: 'monospace', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{maskSensitiveIdentifier(row.external_reference)}</td>
        </tr>;
        })}
        {!loading && !rows.length && <tr><td colSpan="9" style={S.empty}>Aucune transaction ne correspond aux filtres.</td></tr>}
      </tbody></table></div>

      {detail && <div style={S.overlay} onClick={closeDetail}><div style={S.modal} onClick={e => e.stopPropagation()}><button style={S.close} onClick={closeDetail}><X size={18}/></button><h2 style={S.title}>Détail de la transaction</h2>{[
        ['Identifiant', detail.id], ['Client', detail.parent_name], ['Téléphone', maskSensitiveIdentifier(detail.parent_phone)], ['Type', detail.kind], ['Produit', detail.product], ['Montant', formatMoney(detail.amount_xaf)], ['Statut', detail.status], ['Suivi', paymentFollowUp(detail).label], ['Fournisseur', detail.provider], ['Moyen de paiement', detail.payment_method], ['Référence TOKKAM', maskSensitiveIdentifier(detail.external_reference)], ['Référence fournisseur', maskSensitiveIdentifier(detail.provider_reference)], ['Pays', detail.country_code], ['Créée le', formatDate(detail.created_at)], ['Payée le', formatDate(detail.paid_at)], ['Abonnement courant', detail.current_subscription_ends_at ? `${detail.current_subscription_status || 'actif'} jusqu'au ${formatDate(detail.current_subscription_ends_at)}` : 'n/a']
      ].map(([label, value]) => <div key={label} style={S.detailRow}><span>{label}</span><strong>{value || 'n/a'}</strong></div>)}</div></div>}
    </div>
  );
}

const S = {
  page: { padding: 20 }, providersSection: { marginBottom: 16 }, sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 10 }, sectionTitle: { margin: 0, fontSize: 16, color: '#fff' }, sectionText: { margin: '5px 0 0', maxWidth: 760, color: 'rgba(255,255,255,.64)', fontSize: 10, lineHeight: 1.5 },
  providerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(275px,1fr))', gap: 10 }, countryCard: { background: '#10224a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 6, padding: 12 }, countryHead: { display: 'flex', alignItems: 'center', gap: 9, paddingBottom: 9, marginBottom: 2, borderBottom: '1px solid rgba(255,255,255,.1)' }, countryCode: { display: 'grid', placeItems: 'center', width: 34, height: 26, borderRadius: 4, background: '#1e4db7', color: '#fff', fontSize: 11, fontWeight: 900 }, countryName: { color: '#fff', fontSize: 12 },
  providerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.08)' }, providerIdentity: { display: 'flex', flexDirection: 'column', minWidth: 0 }, providerName: { color: '#fff', fontSize: 11 }, providerKey: { color: 'rgba(255,255,255,.48)', fontFamily: 'monospace', fontSize: 9, marginTop: 3 }, providerNote: { color: 'rgba(255,255,255,.66)', fontSize: 9, marginTop: 3 }, providerStatus: { flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 4, padding: '4px 6px', fontSize: 8, fontWeight: 900, whiteSpace: 'nowrap' },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 14 },
  kpi: { background: '#10224a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 6, padding: 14, minHeight: 82, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
  kpiLabel: { color: 'rgba(255,255,255,.72)', fontSize: 10, textTransform: 'uppercase', fontWeight: 800 }, kpiValue: { fontSize: 21, marginTop: 8 },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }, searchWrap: { display: 'flex', alignItems: 'center', gap: 8, background: '#10224a', border: '1px solid rgba(255,255,255,.18)', borderRadius: 6, padding: '0 10px', color: '#fff' },
  search: { width: 250, border: 0, outline: 0, background: 'transparent', color: '#fff', padding: '9px 0', fontFamily: 'monospace' }, control: { background: '#10224a', color: '#fff', border: '1px solid rgba(255,255,255,.18)', borderRadius: 6, padding: '9px 10px', fontFamily: 'monospace' },
  primary: { background: '#f5a623', color: '#0d1b3e', border: 0, borderRadius: 6, padding: '9px 14px', fontWeight: 900, cursor: 'pointer' }, iconBtn: { display: 'grid', placeItems: 'center', background: '#1e4db7', color: '#fff', border: 0, borderRadius: 6, width: 36, height: 36, cursor: 'pointer' }, exportBtn: { display: 'flex', alignItems: 'center', gap: 7, background: '#22c55e', color: '#071a12', border: 0, borderRadius: 6, padding: '9px 13px', fontWeight: 900, cursor: 'pointer' },
  attentionBtn: { display: 'flex', alignItems: 'center', gap: 7, background: '#3a3040', color: '#f5a623', border: '1px solid rgba(245,166,35,.35)', borderRadius: 6, padding: '9px 13px', fontWeight: 900, cursor: 'pointer' },
  attentionBtnActive: { display: 'flex', alignItems: 'center', gap: 7, background: '#f5a623', color: '#0d1b3e', border: 0, borderRadius: 6, padding: '9px 13px', fontWeight: 900, cursor: 'pointer' },
  meta: { color: 'rgba(255,255,255,.72)', fontSize: 11, margin: '8px 0 10px' },
  pagination: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 10px', color: '#fff' },
  pageInfo: { color: 'rgba(255,255,255,.72)', fontSize: 11, fontWeight: 800 },
  pageSizeLabel: { display: 'flex', alignItems: 'center', gap: 7, color: 'rgba(255,255,255,.72)', fontSize: 11, fontWeight: 800 },
  pageSizeSelect: { background: '#10224a', color: '#fff', border: '1px solid rgba(255,255,255,.18)', borderRadius: 6, padding: '7px 9px', fontFamily: 'monospace', fontWeight: 900 },
  pageButton: { background: '#1e4db7', color: '#fff', border: 0, borderRadius: 6, padding: '8px 12px', fontWeight: 900, cursor: 'pointer' },
  tableCard: { background: '#10224a', border: '1px solid rgba(255,255,255,.16)', borderRadius: 6, overflow: 'auto' }, table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#fff', fontSize: 10, letterSpacing: .7, padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,.16)', whiteSpace: 'nowrap' }, td: { color: '#fff', fontSize: 11, padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,.09)', verticalAlign: 'middle' }, tr: { cursor: 'pointer' }, attentionTr: { cursor: 'pointer', background: 'rgba(245,166,35,.08)' }, sub: { color: 'rgba(255,255,255,.65)', marginTop: 3 }, badge: { display: 'inline-block', borderRadius: 4, padding: '3px 7px', fontWeight: 900 }, empty: { color: '#fff', textAlign: 'center', padding: 24 },
  overlay: { position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(0,0,0,.76)', display: 'grid', placeItems: 'center', padding: 20 }, modal: { width: 'min(620px,100%)', maxHeight: '85vh', overflow: 'auto', position: 'relative', background: '#10224a', border: '1px solid rgba(255,255,255,.2)', borderRadius: 8, padding: 22, boxShadow: '0 30px 90px rgba(0,0,0,.55)' }, close: { position: 'absolute', right: 14, top: 14, border: 0, background: 'transparent', color: '#fff', cursor: 'pointer' }, title: { margin: '0 0 18px', fontSize: 18 }, detailRow: { display: 'grid', gridTemplateColumns: '150px 1fr', gap: 12, padding: '9px 0', borderTop: '1px solid rgba(255,255,255,.1)', color: '#fff', fontSize: 11, wordBreak: 'break-word' },
};
